import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';
import React, { useRef } from 'react';

function TiltCard({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const rotateX = useSpring(useTransform(y, [-0.5, 0.5], [8, -8]), {
    stiffness: 200,
    damping: 20,
  });
  const rotateY = useSpring(useTransform(x, [-0.5, 0.5], [-8, 8]), {
    stiffness: 200,
    damping: 20,
  });

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    x.set((e.clientX - rect.left) / rect.width - 0.5);
    y.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{ rotateX, rotateY, transformPerspective: 800 }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export const ThreeDFlythrough = () => {
  const { t } = useLocale('ai-interior');

  const features = [
    {
      label: t('Stage the empty room first'),
      description: t(
        'Upload a photo of your room and let AI clear out the existing furniture, then virtually stage it in any design style — Scandinavian, Japandi, Mid-Century, you name it.',
      ),
    },
    {
      label: t('Cinematic walkthrough video'),
      description: t(
        'Turn the staged room into a smooth video tour with multiple camera moves — perfect for sharing with family, your contractor, or potential buyers.',
      ),
    },
  ];

  return (
    <section className='bg-white py-20 dark:bg-slate-800 sm:py-28'>
      <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
        <div className='text-center'>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
            className='text-3xl text-center mb-4 font-black tracking-tight leading-tight bg-gradient-to-r from-purple-600 via-red-500 to-primary-600 bg-clip-text text-transparent pb-2 sm:text-5xl'
          >
            {t('From empty room to cinematic walkthrough')}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
            className='mx-auto mt-4 max-w-2xl text-lg text-gray-600 dark:text-gray-300'
          >
            {t(
              'Upload a single room photo, virtually stage it in any design style, and turn the result into a cinematic video walkthrough — all in one workflow.',
            )}
          </motion.p>
        </div>
        <div className='mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2'>
          {features.map((feature, i) => (
            <TiltCard
              key={i}
              className='relative rounded-2xl border border-gray-200 bg-white p-8 shadow-sm transition-shadow duration-300 hover:shadow-xl dark:border-gray-700 dark:bg-slate-800/50'
            >
              <div className='absolute -top-4 left-4 inline-flex items-center rounded-full bg-gradient-to-r from-primary-500 to-primary-600 px-4 py-1.5 text-sm font-semibold text-white shadow-md shadow-primary-500/20'>
                {feature.label}
              </div>
              <p className='mt-4 text-base leading-relaxed text-gray-600 dark:text-gray-300'>
                {feature.description}
              </p>
            </TiltCard>
          ))}
        </div>
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.97 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          viewport={{ once: true }}
          className='mt-16'
        >
          <div className='group relative mx-auto max-w-4xl overflow-hidden rounded-2xl shadow-2xl ring-1 ring-black/5'>
            <div className='aspect-w-16 aspect-h-9'>
              <video
                className='w-full object-cover'
                autoPlay
                loop
                muted
                playsInline
                crossOrigin='anonymous'
                preload='auto'
              >
                <source
                  src='https://media.easy-peasy.ai/assets/original_df4bfdfb-0c93-4c80-a43e-4cb05293eb4f.mp4'
                  type='video/mp4'
                />
                <p className='text-center text-gray-600 dark:text-gray-300'>
                  {t('Your browser does not support the video tag.')}
                </p>
              </video>
            </div>
            <div className='pointer-events-none absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent' />
            <div className='absolute bottom-6 left-6 right-6'>
              <p className='text-lg font-semibold text-white'>
                {t('From empty room to listing-ready video')}
              </p>
              <p className='mt-1.5 text-sm text-gray-300'>
                {t(
                  'A single room photo, virtually staged and animated into a cinematic walkthrough.',
                )}
              </p>
            </div>
          </div>
          <div className='mt-10 flex justify-center'>
            <Link
              href='/workflows/templates/realtor-listing-video-virtual-staging'
              className='inline-flex items-center justify-center rounded-full bg-primary-600 px-8 py-3.5 text-base font-semibold text-white shadow-lg shadow-primary-500/20 transition-all duration-300 hover:bg-primary-700 hover:shadow-xl hover:shadow-primary-500/30 hover:scale-[1.03] active:scale-[0.98]'
            >
              {t('Try this workflow')}
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
